import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Factory, Plus, Lock, PackageSearch, Layers, Pencil, Scale, Package, Hash, Cog, CalendarClock, Users, ArrowUp, ArrowDown, History, GripVertical, ArrowUpDown, Eye, Printer, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { unitLabel, labelPianificati, labelProdotti, labelPerPedana, imballoOf } from "@/lib/imballo";
import { SHIFTS, SHIFT_LABEL } from "@/lib/turni";

const emptyOrd = {
    articolo_codice: "", article_id: "", cartoni_pianificati: "", note: "",
    piste_override: "", lunghezza_override: "", buste_per_rotolo_override: "",
    rotoli_per_cartone_override: "", fascetta_id_override: "",
    tipo_pedana_id_override: "", cartone_id_override: "", cartoni_per_pedana_override: "",
    imballo_override: "cartoni",
    cliente_override: "",
    larghezza_principale_override: "", larghezza_soffietto_override: "",
    misura_dichiarata_larghezza_override: "", misura_dichiarata_altezza_override: "",
    impianto_id: "", turno: "", data_lavorazione: "", operatore_id: "",
    multi_operatore: false,
};

function breakdownPedane(qty, cpp, imb) {
    const unit = imb === "vaschette" ? "vaschette" : "cartoni";
    if (!qty || qty <= 0) return "";
    if (!cpp || cpp <= 0 || qty < cpp) return `${qty} ${unit}`;
    const p = Math.floor(qty / cpp);
    const r = qty % cpp;
    const pStr = `${p} pedan${p === 1 ? "a" : "e"}`;
    return r > 0 ? `${pStr} + ${r} ${unit}` : pStr;
}
function calcPesi(art, lungOverride, bprOverride, rpcOverride, wTotOverride) {
    if (!art) return {};
    const l = parseInt(lungOverride) || art.lunghezza || 0;
    const w = wTotOverride != null && wTotOverride > 0
        ? wTotOverride
        : (art.larghezza_principale || 0) + (art.larghezza_soffietto || 0);
    const s = art.spessore_my || 0;
    if (!l || !w || !s) return { wTot: w };
    const bustaRaw = l * w * s * 0.000092 * 2;
    const bpr = parseInt(bprOverride) || art.buste_per_rotolo || 0;
    const rpc = parseInt(rpcOverride) || art.rotoli_per_cartone || 0;
    const rotoloRaw = bustaRaw * bpr;
    const cartoneRaw = rotoloRaw * rpc / 1000;
    return {
        wTot: w,
        busta: bustaRaw > 0 ? bustaRaw.toFixed(2) : null,         // 2 decimals
        rotolo: rotoloRaw > 0 ? Math.round(rotoloRaw).toString() : null,  // intero
        cartone: cartoneRaw > 0 ? cartoneRaw.toFixed(1) : null,   // 1 decimal
    };
}

export default function OrdiniProduzione() {
    const { user } = useAuth();
    const canCreate = user?.role === "admin" || user?.role === "ufficio";
    const [articles, setArticles] = useState([]);
    const [fascette, setFascette] = useState([]);
    const [tipiPed, setTipiPed] = useState([]);
    const [cartoniAnag, setCartoniAnag] = useState([]);
    const [impianti, setImpianti] = useState([]);
    const [operatori, setOperatori] = useState([]);
    const [orders, setOrders] = useState([]);
    const [statoFilter, setStatoFilter] = useState("aperto"); // 'aperto' | 'chiuso' | 'tutti'
    const [searchQ, setSearchQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [openDlg, setOpenDlg] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState(emptyOrd);

    const load = async () => {
        setLoading(true);
        try {
            const [a, f, t, c, i, op, o] = await Promise.all([
                api.get("/articles"), api.get("/fascette/"), api.get("/tipi-pedana/"),
                api.get("/cartoni/"), api.get("/impianti/"), api.get("/users/operatori"),
                api.get("/ordini"),
            ]);
            setArticles(a.data); setFascette(f.data); setTipiPed(t.data);
            setCartoniAnag(c.data); setImpianti(i.data); setOperatori(op.data);
            setOrders(o.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const selArt = useMemo(() => {
        if (editId && form.article_id) {
            const byId = articles.find(a => a.id === form.article_id);
            if (byId) return byId;
        }
        const q = (form.articolo_codice || "").trim().toUpperCase();
        if (!q) return null;
        const bare = q.split(/[\s—]/)[0];
        return articles.find(a => a.codice.toUpperCase() === bare) || null;
    }, [form.articolo_codice, form.article_id, articles, editId]);

    useEffect(() => {
        if (!selArt || editId) return;
        setForm(f => ({
            ...f,
            article_id: selArt.id,
            piste_override: f.piste_override || (selArt.piste_default ? String(selArt.piste_default) : ""),
            lunghezza_override: f.lunghezza_override || (selArt.lunghezza != null ? String(selArt.lunghezza) : ""),
            buste_per_rotolo_override: f.buste_per_rotolo_override || (selArt.buste_per_rotolo != null ? String(selArt.buste_per_rotolo) : ""),
            rotoli_per_cartone_override: f.rotoli_per_cartone_override || (selArt.rotoli_per_cartone != null ? String(selArt.rotoli_per_cartone) : ""),
            fascetta_id_override: f.fascetta_id_override || (selArt.fascetta_id || ""),
            tipo_pedana_id_override: f.tipo_pedana_id_override || (selArt.tipo_pedana_id || ""),
            cartone_id_override: f.cartone_id_override || (selArt.cartone_id || ""),
            cartoni_per_pedana_override: f.cartoni_per_pedana_override || (selArt.cartoni_per_pedana_default != null ? String(selArt.cartoni_per_pedana_default) : ""),
            cliente_override: f.cliente_override || (selArt.cliente_nome || ""),
            larghezza_principale_override: f.larghezza_principale_override || (selArt.larghezza_principale != null ? String(selArt.larghezza_principale) : ""),
            larghezza_soffietto_override: f.larghezza_soffietto_override || (selArt.larghezza_soffietto != null ? String(selArt.larghezza_soffietto) : "0"),
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selArt?.id, editId]);

    const isChanged = (formKey, artKey) => {
        if (!selArt) return true;
        const fv = form[formKey];
        if (fv === "" || fv == null) return false;
        const av = selArt[artKey];
        return String(fv) !== String(av ?? "");
    };

    const qty = parseInt(form.cartoni_pianificati) || 0;
    const cppEff = parseInt(form.cartoni_per_pedana_override) || (selArt?.cartoni_per_pedana_default || 0);
    const imbEff = form.imballo_override || selArt?.imballo || "cartoni";
    const isVas = imbEff === "vaschette";
    const unitAbbr = isVas ? "vasc." : "cart.";
    const packSing = isVas ? "vaschetta" : "cartone";
    const packTitle = isVas ? "Vaschetta" : "Cartone";
    const breakdown = breakdownPedane(qty, cppEff, imbEff);
    // Vincolo somma larghezze: la somma deve restare identica a quella dell'articolo
    const wTargetSum = selArt ? (selArt.larghezza_principale || 0) + (selArt.larghezza_soffietto || 0) : 0;
    const wCurrentSum = (parseInt(form.larghezza_principale_override) || 0) + (parseInt(form.larghezza_soffietto_override) || 0);
    const wSumOk = !selArt || wCurrentSum === wTargetSum;
    // Pesi live: usa la somma target (che è invariante) — così il peso non cambia anche se ripartisci
    const pesi = calcPesi(selArt, form.lunghezza_override, form.buste_per_rotolo_override, form.rotoli_per_cartone_override, wTargetSum);

    const defaultImpiantoId = useMemo(() => {
        if (!impianti.length) return "";
        return "";  // Non preseleziona più di default; sarà l'articolo a suggerire
    }, [impianti]);

    // Se l'articolo ha una sola macchina compatibile, preselezionala automaticamente al cambio articolo
    useEffect(() => {
        if (!selArt || editId) return;
        const comp = selArt.macchine_compatibili || [];
        if (comp.length === 1) {
            setForm(f => ({...f, impianto_id: f.impianto_id || comp[0]}));
        } else if (comp.length > 1) {
            // 2+: se l'attuale non è tra le compatibili, azzera
            setForm(f => (comp.includes(f.impianto_id) ? f : {...f, impianto_id: ""}));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selArt?.id, editId]);

    const openNew = () => {
        if (articles.length === 0) {
            toast.error("Nessun articolo in anagrafica. Vai su Anagrafiche → Articoli e creane almeno uno prima di aprire un ordine.");
            return;
        }
        setEditId(null); setForm({ ...emptyOrd, impianto_id: defaultImpiantoId }); setOpenDlg(true);
    };
    const [readOnlyView, setReadOnlyView] = useState(false);
    const openEdit = (o, readonly = false) => {
        const art = articles.find(a => a.id === o.article_id);
        setEditId(o.id);
        setReadOnlyView(readonly);
        setForm({
            articolo_codice: art?.codice || o.codice || "",
            article_id: o.article_id,
            cartoni_pianificati: String(o.cartoni_pianificati || ""),
            note: o.note || "",
            piste_override: o.piste_eff != null ? String(o.piste_eff) : "",
            lunghezza_override: o.lunghezza_eff != null ? String(o.lunghezza_eff) : "",
            buste_per_rotolo_override: o.buste_per_rotolo_eff != null ? String(o.buste_per_rotolo_eff) : "",
            rotoli_per_cartone_override: o.rotoli_per_cartone_eff != null ? String(o.rotoli_per_cartone_eff) : "",
            fascetta_id_override: o.fascetta_id_eff || "",
            tipo_pedana_id_override: o.tipo_pedana_id_eff || "",
            cartone_id_override: o.cartone_id_eff || "",
            cartoni_per_pedana_override: o.cartoni_per_pedana_eff != null ? String(o.cartoni_per_pedana_eff) : "",
            imballo_override: o.imballo_eff || o.imballo || "cartoni",
            cliente_override: o.cliente_eff || "",
            larghezza_principale_override: o.larghezza_principale_eff != null ? String(o.larghezza_principale_eff) : "",
            larghezza_soffietto_override: o.larghezza_soffietto_eff != null ? String(o.larghezza_soffietto_eff) : "0",
            misura_dichiarata_larghezza_override: o.misura_dichiarata_larghezza_override != null ? String(o.misura_dichiarata_larghezza_override) : "",
            misura_dichiarata_altezza_override: o.misura_dichiarata_altezza_override != null ? String(o.misura_dichiarata_altezza_override) : "",
            impianto_id: o.impianto_id || "",
            turno: o.turno || "",
            data_lavorazione: o.data_lavorazione || "",
            operatore_id: o.operatore_id || "",
            multi_operatore: !!o.multi_operatore,
        });
        setOpenDlg(true);
    };

    const [storicoOrder, setStoricoOrder] = useState(null);
    const [storicoData, setStoricoData] = useState([]);
    const openStorico = async (o) => {
        setStoricoOrder(o); setStoricoData([]);
        try { const { data } = await api.get(`/ordini/${o.id}/storico`); setStoricoData(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    // Cluster map: chiave = "data|turno|impianto" → lista ordini in quel cluster ordinati per sort_in_shift/created_at
    const clusterMap = useMemo(() => {
        const m = new Map();
        for (const o of orders) {
            if (o.stato !== "aperto") continue;
            if (!o.data_lavorazione || !o.turno || !o.impianto_id) continue;
            const key = `${o.data_lavorazione}|${o.turno}|${o.impianto_id}`;
            if (!m.has(key)) m.set(key, []);
            m.get(key).push(o);
        }
        for (const arr of m.values()) {
            arr.sort((a,b) => (a.sort_in_shift||9999) - (b.sort_in_shift||9999));
        }
        return m;
    }, [orders]);

    const moveInShift = async (o, dir) => {
        try { await api.post(`/ordini/${o.id}/move-shift?direction=${dir}`); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    // Filtri combinati (stato + ricerca full-text)
    const visibleOrders = useMemo(() => {
        const q = searchQ.trim().toLowerCase();
        return orders.filter(o => {
            if (statoFilter !== "tutti" && o.stato !== statoFilter) return false;
            if (!q) return true;
            return (o.numero || "").toLowerCase().includes(q)
                || (o.codice || "").toLowerCase().includes(q)
                || (o.descrizione || "").toLowerCase().includes(q)
                || (o.cliente_eff || "").toLowerCase().includes(q)
                || (o.operatore_nome || "").toLowerCase().includes(q);
        });
    }, [orders, statoFilter, searchQ]);
    const countAperti = useMemo(() => orders.filter(o => o.stato === "aperto").length, [orders]);
    const countChiusi = useMemo(() => orders.filter(o => o.stato === "chiuso").length, [orders]);

    // Raggruppa ordini chiusi per data di chiusura (giorno solare locale), più recenti sopra.
    const chiusiGrouped = useMemo(() => {
        if (statoFilter !== "chiuso") return null;
        const groups = new Map(); // key = yyyy-mm-dd
        for (const o of visibleOrders) {
            let key = "senza-data";
            let d = null;
            if (o.chiuso_at) {
                d = new Date(o.chiuso_at);
                if (!isNaN(d.getTime())) {
                    key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
                }
            }
            if (!groups.has(key)) groups.set(key, { key, date: d, orders: [] });
            groups.get(key).orders.push(o);
        }
        const arr = Array.from(groups.values());
        arr.sort((a, b) => {
            if (a.key === "senza-data") return 1;
            if (b.key === "senza-data") return -1;
            return b.key.localeCompare(a.key);
        });
        // Etichetta leggibile (Mercoledì 5 febbraio 2026)
        for (const g of arr) {
            if (g.date) {
                g.label = g.date.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
                g.label = g.label.charAt(0).toUpperCase() + g.label.slice(1);
            } else {
                g.label = "Senza data di chiusura";
            }
        }
        return arr;
    }, [visibleOrders, statoFilter]);

    // Set di gruppi collassati: di default TUTTI tranne il più recente (che appare espanso).
    const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
    const lastSignatureRef = useRef("");
    useEffect(() => {
        if (!chiusiGrouped) return;
        const sig = chiusiGrouped.map(g => g.key).join("|");
        if (sig === lastSignatureRef.current) return;
        lastSignatureRef.current = sig;
        // Default: tutti collassati eccetto il primo (più recente)
        const s = new Set(chiusiGrouped.map(g => g.key));
        if (chiusiGrouped[0]) s.delete(chiusiGrouped[0].key);
        setCollapsedGroups(s);
    }, [chiusiGrouped]);
    const toggleGroup = (key) => setCollapsedGroups(prev => {
        const n = new Set(prev);
        if (n.has(key)) n.delete(key); else n.add(key);
        return n;
    });
    const [dragId, setDragId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const onDragStart = (e, id) => { setDragId(id); e.dataTransfer.effectAllowed = "move"; };
    const onDragOver = (e, id) => { e.preventDefault(); if (id !== dragOverId) setDragOverId(id); };
    const onDrop = async (e, targetId) => {
        e.preventDefault();
        setDragOverId(null);
        if (!dragId || dragId === targetId) { setDragId(null); return; }
        // Ricostruisce l'array riordinato localmente
        const arr = [...orders];
        const from = arr.findIndex(o => o.id === dragId);
        const to = arr.findIndex(o => o.id === targetId);
        if (from < 0 || to < 0) return;
        const [moved] = arr.splice(from, 1);
        arr.splice(to, 0, moved);
        setOrders(arr); // optimistic UI
        setDragId(null);
        try { await api.post("/ordini/reorder", { ids: arr.map(o => o.id) }); load(); }
        catch (err) { toast.error(formatApiError(err)); load(); }
    };

    const save = async () => {
        if (!selArt) { toast.error("Seleziona un articolo valido dal codice"); return; }
        if (!qty || qty <= 0) { toast.error("Quantità cartoni pianificati obbligatoria"); return; }
        if (!wSumOk) { toast.error(`La somma larg.principale + soffietto deve restare ${wTargetSum} cm`); return; }
        try {
            const numOrNull = (k, artKey) => isChanged(k, artKey) ? parseInt(form[k]) : null;
            const strOrNull = (k, artKey) => isChanged(k, artKey) ? form[k] : null;
            const payload = {
                article_id: selArt.id,
                cartoni_pianificati: qty,
                note: form.note || null,
                piste_override: numOrNull("piste_override", "piste_default"),
                lunghezza_override: numOrNull("lunghezza_override", "lunghezza"),
                buste_per_rotolo_override: numOrNull("buste_per_rotolo_override", "buste_per_rotolo"),
                rotoli_per_cartone_override: numOrNull("rotoli_per_cartone_override", "rotoli_per_cartone"),
                fascetta_id_override: strOrNull("fascetta_id_override", "fascetta_id"),
                tipo_pedana_id_override: strOrNull("tipo_pedana_id_override", "tipo_pedana_id"),
                cartone_id_override: strOrNull("cartone_id_override", "cartone_id"),
                cartoni_per_pedana_override: numOrNull("cartoni_per_pedana_override", "cartoni_per_pedana_default"),
                imballo_override: form.imballo_override || null,
                cliente_override: strOrNull("cliente_override", "cliente_nome"),
                larghezza_principale_override: numOrNull("larghezza_principale_override", "larghezza_principale"),
                larghezza_soffietto_override: numOrNull("larghezza_soffietto_override", "larghezza_soffietto"),
                misura_dichiarata_larghezza_override: form.misura_dichiarata_larghezza_override === "" ? null : parseInt(form.misura_dichiarata_larghezza_override),
                misura_dichiarata_altezza_override: form.misura_dichiarata_altezza_override === "" ? null : parseInt(form.misura_dichiarata_altezza_override),
                impianto_id: form.impianto_id || null,
                turno: form.turno || null,
                data_lavorazione: form.data_lavorazione || null,
                operatore_id: form.operatore_id || null,
                multi_operatore: !!form.multi_operatore,
            };
            let created = null;
            if (editId) { await api.put(`/ordini/${editId}`, payload); toast.success("Ordine aggiornato"); }
            else {
                const { data } = await api.post("/ordini", payload);
                created = data;
                toast.success("Ordine di produzione creato");
            }
            setOpenDlg(false); setEditId(null); setForm(emptyOrd);
            await load();
            // Se ho appena creato in un cluster che ora ha ≥ 2 ordini → apro il dialog riordino
            if (created && created.data_lavorazione && created.turno && created.impianto_id) {
                const { data: refreshed } = await api.get(`/ordini?impianto_id=${created.impianto_id}&stato=aperto`);
                const cluster = refreshed.filter(o =>
                    o.data_lavorazione === created.data_lavorazione &&
                    o.turno === created.turno &&
                    o.impianto_id === created.impianto_id
                ).sort((a,b) => (a.sort_in_shift||9999) - (b.sort_in_shift||9999) || (a.created_at||"").localeCompare(b.created_at||""));
                if (cluster.length >= 2) setClusterReorder(cluster);
            }
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const [clusterReorder, setClusterReorder] = useState(null);
    const [crDragId, setCrDragId] = useState(null);
    const crMove = (fromIdx, toIdx) => {
        const arr = [...clusterReorder];
        const [m] = arr.splice(fromIdx, 1);
        arr.splice(toIdx, 0, m);
        setClusterReorder(arr);
    };
    const saveClusterOrder = async () => {
        try {
            await api.post("/ordini/reorder-cluster", { ids: clusterReorder.map(o => o.id) });
            toast.success("Ordine del turno aggiornato");
            setClusterReorder(null); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Apre il dialog "Ordina il turno" per un ordine esistente (dalla riga)
    const openClusterReorder = async (o) => {
        if (!o.data_lavorazione || !o.turno || !o.impianto_id) {
            toast.error("L'ordine deve avere data, turno e macchina");
            return;
        }
        try {
            const { data } = await api.get(`/ordini?impianto_id=${o.impianto_id}&stato=aperto`);
            const cluster = data.filter(x =>
                x.data_lavorazione === o.data_lavorazione &&
                x.turno === o.turno &&
                x.impianto_id === o.impianto_id
            ).sort((a,b) => (a.sort_in_shift||9999) - (b.sort_in_shift||9999) || (a.created_at||"").localeCompare(b.created_at||""));
            if (cluster.length < 2) { toast.info("Nessun altro ordine nello stesso turno"); return; }
            setClusterReorder(cluster);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const chiudi = async (o) => {
        if (!window.confirm(`Chiudere definitivamente ordine ${o.numero}?`)) return;
        try { await api.post(`/ordini/${o.id}/chiudi`); toast.success("Ordine chiuso"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <div className="text-[10px] tracking-[0.3em] font-mono text-amber-500/80">UFFICIO PRODUZIONE</div>
                    <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Ordini di produzione saldatrici</h1>
                    <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                        Inserisci il codice articolo per richiamare automaticamente i dati presenti in anagrafica.<br/>
                        Alcuni dati possono essere modificati esclusivamente per il singolo ordine, senza modificare l'articolo in anagrafica. Per modificare in modo permanente le caratteristiche dell'articolo, intervieni direttamente nella relativa anagrafica.<br/>
                        L'ordine può essere modificato solo fino all'avvio della produzione. Una volta avviata la produzione, l'ordine non sarà più modificabile.
                    </p>
                </div>
                {canCreate && (
                    <Button onClick={openNew} data-testid="button-new-order"
                            title={articles.length === 0 ? "Crea prima almeno un articolo in Anagrafiche → Articoli" : ""}
                            className="bg-amber-500 hover:bg-amber-400 text-slate-950 gap-2">
                        <Plus size={16} /> Nuovo ordine
                    </Button>
                )}
            </div>

            <Card className="card overflow-hidden">
                <div className="px-5 py-3 border-b border-slate-800 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex gap-2" data-testid="filter-stato">
                        <button onClick={()=>setStatoFilter("aperto")} data-testid="filter-aperti"
                                className={`px-3 py-1.5 rounded-md text-xs font-medium ${statoFilter==="aperto" ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-100"}`}>
                            Aperti ({countAperti})
                        </button>
                        <button onClick={()=>setStatoFilter("chiuso")} data-testid="filter-chiusi"
                                className={`px-3 py-1.5 rounded-md text-xs font-medium ${statoFilter==="chiuso" ? "bg-slate-500/20 text-slate-300 border border-slate-500/40" : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-100"}`}>
                            Chiusi ({countChiusi})
                        </button>
                        <button onClick={()=>setStatoFilter("tutti")} data-testid="filter-tutti"
                                className={`px-3 py-1.5 rounded-md text-xs font-medium ${statoFilter==="tutti" ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-800 text-slate-400 border border-slate-700 hover:text-slate-100"}`}>
                            Tutti ({orders.length})
                        </button>
                    </div>
                    <div className="md:ml-auto md:w-72">
                        <Input data-testid="input-search-ordini" value={searchQ}
                               onChange={e=>setSearchQ(e.target.value)}
                               placeholder="Cerca numero, codice, cliente, descrizione, operatore…"
                               className="bg-slate-950/50 border-slate-800 text-sm" />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Numero</th>
                                <th className="text-left px-5 py-3">Articolo</th>
                                <th className="text-right px-5 py-3">Piano</th>
                                <th className="text-right px-5 py-3">Prodotti</th>
                                <th className="text-left px-5 py-3">Lavorazione</th>
                                <th className="text-left px-5 py-3">Stato</th>
                                {canCreate && <th className="text-right px-5 py-3">Azioni</th>}
                            </tr>
                        </thead>
                        <tbody data-testid="table-ordini">
                            {loading && <tr><td colSpan={7} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && orders.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">
                                    <Factory size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessun ordine di produzione</div>
                                </td></tr>
                            )}
                            {!loading && orders.length > 0 && visibleOrders.length === 0 && (
                                <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-500">
                                    <Factory size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">
                                        {searchQ
                                            ? `Nessun ordine trovato con "${searchQ}"`
                                            : `Nessun ordine ${statoFilter === "aperto" ? "aperto" : "chiuso"}`}
                                    </div>
                                </td></tr>
                            )}
                            {(() => {
                                const renderRow = (o) => {
                                    const bd = breakdownPedane(o.cartoni_pianificati, o.cartoni_per_pedana_eff, imballoOf(o));
                                    const canEditOrder = o.stato === "aperto" && (o.cartoni_prodotti || 0) === 0;
                                    const isVas = imballoOf(o) === "vaschette";
                                    return (
                                    <tr key={o.id}
                                        className="border-b border-slate-800/60 hover:bg-slate-800/30"
                                        data-testid={`row-order-${o.numero}`}>
                                        {canCreate && (() => {
                                            // Colonna Azioni: storico + edit + chiudi
                                            return null;
                                        })()}
                                        <td className="px-5 py-3 doc-code text-xs">
                                            {o.numero}
                                            {o.auto && <Badge className="ml-2 badge-pill bg-violet-500/15 text-violet-300 border-violet-500/40">AUTO</Badge>}
                                            {o.multi_operatore && <Badge className="ml-2 badge-pill bg-amber-500/15 text-amber-300 border-amber-500/40">MULTI-OP</Badge>}
                                            {isVas && <Badge className="ml-2 badge-pill bg-teal-500/15 text-teal-300 border-teal-500/40">VASCHETTE</Badge>}
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="font-mono text-xs text-amber-300">{o.codice}</div>
                                            <div className="text-slate-200 text-sm">{o.descrizione}</div>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="font-mono">{o.cartoni_pianificati}</div>
                                            {bd && bd !== `${o.cartoni_pianificati} ${unitLabel(o)}` && (
                                                <div className="text-[10px] text-slate-500 font-mono">= {bd}</div>
                                            )}
                                        </td>
                                        <td className="px-5 py-3 text-right font-mono text-emerald-300">{o.cartoni_prodotti}</td>
                                        <td className="px-5 py-3 text-xs">
                                            <div className="flex flex-col gap-0.5">
                                                {o.impianto_nome && <span className="text-slate-300"><Cog size={10} className="inline mr-1" />{o.impianto_nome}</span>}
                                                {o.operatore_nome && <span className="text-slate-400">👤 {o.operatore_nome}</span>}
                                                {(o.turno || o.data_lavorazione) && (
                                                    <span className="text-slate-500 font-mono">
                                                        {o.data_lavorazione && new Date(o.data_lavorazione).toLocaleDateString("it-IT")}
                                                        {o.turno && ` · ${SHIFT_LABEL[o.turno] || `Turno ${o.turno}`}`}
                                                    </span>
                                                )}
                                                {!o.impianto_nome && !o.operatore_nome && !o.turno && !o.data_lavorazione && <span className="text-slate-600">—</span>}
                                            </div>
                                        </td>
                                        <td className="px-5 py-3">
                                            {o.stato === "aperto"
                                                ? <Badge className="badge-pill bg-amber-500/15 text-amber-300 border-amber-500/30">APERTO</Badge>
                                                : <Badge className="badge-pill bg-slate-500/15 text-slate-300 border-slate-500/30">CHIUSO</Badge>}
                                        </td>
                                        {canCreate && (
                                            <td className="px-5 py-3 text-right whitespace-nowrap">
                                                {(() => {
                                                    const key = (o.data_lavorazione && o.turno && o.impianto_id) ? `${o.data_lavorazione}|${o.turno}|${o.impianto_id}` : null;
                                                    const cluster = key ? (clusterMap.get(key) || []) : [];
                                                    if (cluster.length < 2 || o.stato !== "aperto") return null;
                                                    return (
                                                        <button onClick={()=>openClusterReorder(o)} data-testid={`button-reorder-${o.numero}`}
                                                                title={`Riordina turno (${cluster.length} ordini)`}
                                                                className="p-2 rounded hover:bg-slate-700/50 text-slate-300">
                                                            <ArrowUpDown size={14} />
                                                        </button>
                                                    );
                                                })()}
                                                <button onClick={()=>openStorico(o)} data-testid={`button-storico-${o.numero}`} title="Vedi storico lavorazioni"
                                                        className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><History size={14} /></button>
                                                {canEditOrder ? (
                                                    <button onClick={()=>openEdit(o, false)} data-testid={`button-edit-${o.numero}`} title="Modifica (produzione non iniziata)"
                                                            className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Pencil size={14} /></button>
                                                ) : (
                                                    <button onClick={()=>openEdit(o, true)} data-testid={`button-view-${o.numero}`} title="Vedi dettagli ordine (sola lettura)"
                                                            className="p-2 rounded hover:bg-slate-700/50 text-slate-300"><Eye size={14} /></button>
                                                )}
                                                {o.stato === "aperto" && (
                                                    <button onClick={()=>window.open(`/stampa-ordine/${o.id}`, "_blank")}
                                                            data-testid={`button-print-a4-${o.numero}`} title="Stampa foglio ordine A4"
                                                            className="p-2 rounded hover:bg-blue-500/20 text-blue-300"><Printer size={14} /></button>
                                                )}
                                                {o.stato === "aperto" && (
                                                    <Button size="sm" variant="outline" onClick={()=>chiudi(o)} data-testid={`button-close-${o.numero}`}
                                                            className="bg-transparent border-slate-700 text-slate-300 gap-2 h-8 ml-1">
                                                        <Lock size={12} /> Chiudi
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    );
                                };
                                // GROUPED MODE (Chiusi per data)
                                if (chiusiGrouped) {
                                    return chiusiGrouped.map(g => {
                                        const collapsed = collapsedGroups.has(g.key);
                                        return (
                                            <React.Fragment key={g.key}>
                                                <tr className="bg-slate-900/60 border-b border-slate-700" data-testid={`chiusi-group-${g.key}`}>
                                                    <td colSpan={canCreate ? 7 : 6} className="px-3 py-2">
                                                        <button type="button" onClick={()=>toggleGroup(g.key)}
                                                                data-testid={`chiusi-group-toggle-${g.key}`}
                                                                className="w-full flex items-center gap-2 text-left hover:bg-slate-800/30 rounded px-2 py-1 transition">
                                                            {collapsed ? <ChevronRight size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-amber-400" />}
                                                            <span className="font-semibold text-slate-200 text-sm">{g.label}</span>
                                                            <span className="text-[10px] font-mono text-slate-500 ml-auto">{g.orders.length} ordin{g.orders.length===1?"e":"i"} chius{g.orders.length===1?"o":"i"}</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {!collapsed && g.orders.map(renderRow)}
                                            </React.Fragment>
                                        );
                                    });
                                }
                                // FLAT MODE (Aperti / Tutti)
                                return visibleOrders.map(renderRow);
                            })()}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Storico lavorazioni (solo ufficio) */}
            <Dialog open={!!clusterReorder} onOpenChange={(v)=>{ if (!v) setClusterReorder(null); }}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <GripVertical size={16} className="text-amber-400" /> Ordina il turno
                        </DialogTitle>
                    </DialogHeader>
                    {clusterReorder && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-400">
                                {clusterReorder.length} ordini condividono <b>{clusterReorder[0].data_lavorazione}</b> · Turno <b>{clusterReorder[0].turno}</b> · <b>{clusterReorder[0].impianto_nome}</b>.
                                Trascina per decidere la sequenza in cui l'operatore li vedrà.
                            </div>
                            <div className="space-y-2" data-testid="cluster-reorder-list">
                                {clusterReorder.map((o, idx) => (
                                    <div key={o.id}
                                         draggable
                                         onDragStart={()=>setCrDragId(o.id)}
                                         onDragOver={(e)=>{ e.preventDefault(); }}
                                         onDrop={()=>{
                                             if (!crDragId || crDragId === o.id) return;
                                             const from = clusterReorder.findIndex(x => x.id === crDragId);
                                             if (from < 0) return;
                                             crMove(from, idx); setCrDragId(null);
                                         }}
                                         onDragEnd={()=>setCrDragId(null)}
                                         data-testid={`cluster-item-${o.numero}`}
                                         className={`flex items-center gap-3 p-3 rounded-md bg-slate-950/50 border border-slate-800 cursor-grab hover:border-amber-500/40 ${crDragId===o.id ? "opacity-40" : ""}`}>
                                        <GripVertical size={16} className="text-slate-500 shrink-0" />
                                        <div className="font-mono text-lg font-bold text-amber-300 w-8">{idx+1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="doc-code text-xs">{o.numero}</div>
                                            <div className="text-sm truncate">{o.codice} · {o.descrizione}</div>
                                        </div>
                                        <div className="text-right font-mono text-xs text-slate-400 shrink-0">{o.cartoni_pianificati} cart.</div>
                                        {/* Frecce touch-friendly per mobile */}
                                        <div className="flex flex-col gap-0.5 shrink-0">
                                            <button data-testid={`cluster-up-${o.numero}`} disabled={idx===0}
                                                    onClick={()=>crMove(idx, idx-1)}
                                                    className="p-1 rounded bg-slate-800 text-slate-300 hover:bg-amber-500 hover:text-slate-950 disabled:opacity-20">
                                                <ArrowUp size={12} />
                                            </button>
                                            <button data-testid={`cluster-down-${o.numero}`} disabled={idx===clusterReorder.length-1}
                                                    onClick={()=>crMove(idx, idx+1)}
                                                    className="p-1 rounded bg-slate-800 text-slate-300 hover:bg-amber-500 hover:text-slate-950 disabled:opacity-20">
                                                <ArrowDown size={12} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setClusterReorder(null)} className="bg-transparent border-slate-700">Salta</Button>
                        <Button onClick={saveClusterOrder} data-testid="button-save-cluster"
                                className="bg-amber-500 hover:bg-amber-400 text-slate-950">Salva ordine turno</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Storico lavorazioni (solo ufficio) */}
            <Dialog open={!!storicoOrder} onOpenChange={(v)=>{ if (!v) setStoricoOrder(null); }}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
                        <History size={18} className="text-amber-400" /> Storico lavorazioni · {storicoOrder?.numero}
                    </DialogTitle></DialogHeader>
                    {storicoOrder && (
                        <div className="space-y-3">
                            <div className="text-xs text-slate-400">
                                <span className="font-mono text-amber-300">{storicoOrder.codice}</span> · {storicoOrder.descrizione} · piano <b>{storicoOrder.cartoni_pianificati}</b> · prodotti <b className="text-emerald-300">{storicoOrder.cartoni_prodotti}</b>
                            </div>
                            <table className="w-full text-sm" data-testid="table-storico">
                                <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                                    <tr>
                                        <th className="text-left py-2">Lotto</th>
                                        <th className="text-left py-2">Operatore</th>
                                        <th className="text-left py-2">Data · Turno</th>
                                        <th className="text-right py-2">Cartoni</th>
                                        <th className="text-left py-2">Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {storicoData.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-slate-500 text-xs">Nessuna lavorazione registrata</td></tr>}
                                    {storicoData.map(l => (
                                        <tr key={l.id} className="border-b border-slate-800/60">
                                            <td className="py-2 doc-code text-xs">
                                                {l.numero}
                                                {l.alternativa && <Badge className="ml-1 badge-pill bg-violet-500/15 text-violet-300 border-violet-500/40 text-[9px]">EXTRA</Badge>}
                                            </td>
                                            <td className="py-2 text-xs text-slate-300">{l.operatore_nome}</td>
                                            <td className="py-2 text-xs text-slate-400 font-mono">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                                            <td className="py-2 text-right font-mono font-bold">{l.cartoni}</td>
                                            <td className="py-2 text-xs text-slate-500 italic truncate max-w-[200px]">{l.note || ""}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={openDlg} onOpenChange={(v)=>{ setOpenDlg(v); if(!v){ setEditId(null); setReadOnlyView(false); setForm(emptyOrd);} }}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-3xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-display">{readOnlyView ? "Dettagli Ordine (sola lettura)" : editId ? "Modifica Ordine di Produzione" : "Nuovo Ordine di Produzione"}</DialogTitle></DialogHeader>
                    <fieldset disabled={readOnlyView} className={readOnlyView ? "opacity-90 pointer-events-none" : ""}>
                    <div className="space-y-5">
                        <div>
                            <Label className="text-xs text-slate-400 flex items-center gap-2">
                                <PackageSearch size={12} /> Codice articolo *
                            </Label>
                            <Input data-testid="input-articolo-codice"
                                   value={form.articolo_codice}
                                   onChange={e=>setForm({...form, articolo_codice: e.target.value.toUpperCase()})}
                                   placeholder="Es. SA3N3R"
                                   list="articoli-list"
                                   disabled={!!editId}
                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono disabled:opacity-70" />
                            <datalist id="articoli-list">
                                {articles.map(a => <option key={a.id} value={a.codice}>{a.descrizione}</option>)}
                            </datalist>
                            <div className="text-[10px] mt-1">
                                {selArt
                                    ? <span className="text-emerald-400">✓ {selArt.descrizione}</span>
                                    : form.articolo_codice
                                        ? <span className="text-red-400">Codice non trovato</span>
                                        : <span className="text-slate-500">Digita il codice o cerca dalla lista</span>}
                            </div>
                        </div>

                        {selArt && (
                            <>
                                {/* RETTANGOLO 1 — Identificativo articolo */}
                                <div className="rounded-lg border border-violet-500/40 bg-violet-500/5 p-4 space-y-3">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300">
                                        <Hash size={12} /> Identificativo articolo
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="rect-identificativo">
                                        {/* Riga 1: Cliente (edit) · Descrizione (span 2) · [spazio residuo occupato da... nulla, allineiamo su 4 col con span 2+2] — usiamo 3 colonne totali */}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div>
                                            <Label className="text-xs text-slate-400">Cliente</Label>
                                            <Input data-testid="input-cliente-override"
                                                   value={form.cliente_override}
                                                   onChange={e=>setForm({...form, cliente_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 ${isChanged("cliente_override","cliente_nome") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div className="md:col-span-2">
                                            <Label className="text-xs text-slate-400">Descrizione</Label>
                                            <Input disabled value={selArt.descrizione || ""} title={selArt.descrizione}
                                                   className="bg-slate-950/50 border-slate-800 mt-1 disabled:opacity-100 disabled:cursor-default" />
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div>
                                            <Label className="text-xs text-slate-400">Larg. principale (cm)</Label>
                                            <Input data-testid="input-larg-principale-override" type="number" min="0"
                                                   value={form.larghezza_principale_override}
                                                   onChange={e=>setForm({...form, larghezza_principale_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("larghezza_principale_override","larghezza_principale") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Soffietto (cm)</Label>
                                            <Input data-testid="input-soffietto-override" type="number" min="0"
                                                   value={form.larghezza_soffietto_override}
                                                   onChange={e=>setForm({...form, larghezza_soffietto_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("larghezza_soffietto_override","larghezza_soffietto") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Lunghezza busta (cm) *</Label>
                                            <Input data-testid="input-lung-override" type="number" min="1"
                                                   value={form.lunghezza_override}
                                                   onChange={e=>setForm({...form, lunghezza_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("lunghezza_override","lunghezza") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Spessore (my)</Label>
                                            <Input disabled value={selArt.spessore_my ?? ""}
                                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono disabled:opacity-100 disabled:cursor-default" />
                                        </div>
                                    </div>
                                    {!wSumOk && (
                                        <div className="text-xs text-red-400 font-mono" data-testid="warning-somma-larghezze">
                                            ⚠️ La somma deve restare {wTargetSum} cm (ora {wCurrentSum})
                                        </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <Label className="text-xs text-slate-400">Colore</Label>
                                            <Input disabled value={selArt.colore_nome || ""}
                                                   className="bg-slate-950/50 border-slate-800 mt-1 disabled:opacity-100 disabled:cursor-default" />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Fascetta</Label>
                                            <Select value={form.fascetta_id_override || ""} onValueChange={v=>setForm({...form, fascetta_id_override: v})}>
                                                <SelectTrigger data-testid="select-fascetta-override"
                                                    className={`bg-slate-950/50 border-slate-800 mt-1 ${isChanged("fascetta_id_override","fascetta_id") ? "border-violet-500/80 border-2" : ""}`}>
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {fascette.map(f => <SelectItem key={f.id} value={f.id}>
                                                        <span className="font-mono text-amber-300 mr-2">{f.codice}</span>{f.descrizione}
                                                    </SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                    {(() => {
                                        const fascEffId = form.fascetta_id_override || selArt?.fascetta_id;
                                        const fascEff = fascette.find(f => f.id === fascEffId);
                                        const fascLarg = fascEff?.larg_dichiarata_cm;
                                        const fascAlt = fascEff?.alt_dichiarata_cm;
                                        const needsOverride = !fascLarg || !fascAlt;
                                        if (!needsOverride) return (
                                            <div className="text-[10px] text-emerald-400/80 font-mono">
                                                ✓ Misura dichiarata su fascetta: {fascLarg} × {fascAlt} cm — in etichetta si stamperà così
                                            </div>
                                        );
                                        return (
                                            <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                                                <div className="text-[10px] text-amber-300 uppercase tracking-widest">
                                                    Misura dichiarata da stampare in etichetta (fascetta senza misura propria)
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <Label className="text-xs text-slate-400">Larghezza dichiarata (cm)</Label>
                                                        <Input data-testid="input-misura-dich-larg" type="number" min="0"
                                                               value={form.misura_dichiarata_larghezza_override}
                                                               onChange={e=>setForm({...form, misura_dichiarata_larghezza_override: e.target.value})}
                                                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                                    </div>
                                                    <div>
                                                        <Label className="text-xs text-slate-400">Altezza dichiarata (cm)</Label>
                                                        <Input data-testid="input-misura-dich-alt" type="number" min="0"
                                                               value={form.misura_dichiarata_altezza_override}
                                                               onChange={e=>setForm({...form, misura_dichiarata_altezza_override: e.target.value})}
                                                               className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                                    </div>
                                                </div>
                                                <div className="text-[10px] text-slate-500">
                                                    Compila solo se serve stampare una misura diversa da quella reale della busta (es. fascetta "55×70" per busta 54×65).
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* RETTANGOLO 2 — Pesi */}
                                <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-4">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 mb-3">
                                        <Scale size={12} /> Pesi (ricalcolati live)
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 text-xs" data-testid="rect-pesi">
                                        <div>
                                            <div className="text-slate-500 text-[10px] uppercase">Peso busta</div>
                                            <div className="mt-0.5 font-mono text-emerald-300">{pesi.busta ? `${pesi.busta} g` : "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-500 text-[10px] uppercase">Peso rotolo</div>
                                            <div className="mt-0.5 font-mono text-emerald-300">{pesi.rotolo ? `${pesi.rotolo} g` : "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-slate-500 text-[10px] uppercase">Peso {packSing}</div>
                                            <div className="mt-0.5 font-mono text-emerald-300 font-bold">{pesi.cartone ? `${pesi.cartone} kg` : "—"}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* RETTANGOLO 3 — Confezionamento (senza Piste) */}
                                <div className="rounded-lg border border-violet-500/40 bg-violet-500/5 p-4 space-y-3">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-violet-300">
                                        <Package size={12} /> Confezionamento
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-400">Imballo</Label>
                                        <div className="flex gap-2 mt-2" data-testid="ord-imballo-group">
                                            {[
                                                {v:"cartoni", l:"Cartoni"},
                                                {v:"vaschette", l:"Vaschette"},
                                            ].map(op => {
                                                const checked = (form.imballo_override || "cartoni") === op.v;
                                                return (
                                                    <button type="button" key={op.v} disabled={readOnlyView}
                                                        data-testid={`ord-imballo-${op.v}`}
                                                        onClick={()=>setForm({...form, imballo_override: op.v})}
                                                        className={`px-4 py-2 rounded-md border text-xs font-semibold transition ${checked ? "bg-violet-500/15 border-violet-500/60 text-violet-200" : "bg-slate-800/50 border-slate-700 text-slate-400 hover:text-slate-100"} disabled:opacity-60`}>
                                                        {op.l}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            Cambia l'etichetta di quantità (cartoni ↔ vaschette) solo per questo ordine.
                                        </div>
                                    </div>
                                    {selArt?.note_articolo && (
                                        <div className="rounded-md border border-slate-700 bg-slate-950/60 p-3">
                                            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Note articolo (sola lettura)</div>
                                            <div className="text-xs text-slate-300 whitespace-pre-wrap" data-testid="ord-note-articolo-ro">{selArt.note_articolo}</div>
                                        </div>
                                    )}
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="rect-confezionamento">
                                        <div>
                                            <Label className="text-xs text-slate-400">Buste per rotolo</Label>
                                            <Input data-testid="input-bpr-override" type="number" min="1"
                                                   value={form.buste_per_rotolo_override}
                                                   onChange={e=>setForm({...form, buste_per_rotolo_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("buste_per_rotolo_override","buste_per_rotolo") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Rotoli per {packSing}</Label>
                                            <Input data-testid="input-rpc-override" type="number" min="1"
                                                   value={form.rotoli_per_cartone_override}
                                                   onChange={e=>setForm({...form, rotoli_per_cartone_override: e.target.value})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("rotoli_per_cartone_override","rotoli_per_cartone") ? "border-violet-500/80 border-2" : ""}`} />
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Pedana</Label>
                                            <Select value={form.tipo_pedana_id_override || ""} onValueChange={v=>{
                                                const patch = { tipo_pedana_id_override: v };
                                                const c = cartoniAnag.find(x => x.id === form.cartone_id_override);
                                                if (c && !form._cpp_manual) {
                                                    const v2 = (c.cartoni_per_pedana_map || {})[v];
                                                    if (v2) patch.cartoni_per_pedana_override = String(v2);
                                                }
                                                setForm({...form, ...patch});
                                            }}>
                                                <SelectTrigger data-testid="select-tped-override"
                                                    className={`bg-slate-950/50 border-slate-800 mt-1 ${isChanged("tipo_pedana_id_override","tipo_pedana_id") ? "border-violet-500/80 border-2" : ""}`}>
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {tipiPed.map(t => <SelectItem key={t.id} value={t.id}>
                                                        <span className="font-mono text-amber-300 mr-2">{t.codice}</span>{t.descrizione}
                                                    </SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {!isVas && (
                                        <div>
                                            <Label className="text-xs text-slate-400">Cartone</Label>
                                            <Select value={form.cartone_id_override || ""} onValueChange={v=>{
                                                const c = cartoniAnag.find(x => x.id === v);
                                                const patch = { cartone_id_override: v };
                                                // Auto-fill dalla matrice cartone × tipo pedana corrente
                                                if (c && form.tipo_pedana_id_override && !form._cpp_manual) {
                                                    const v2 = (c.cartoni_per_pedana_map || {})[form.tipo_pedana_id_override];
                                                    if (v2) patch.cartoni_per_pedana_override = String(v2);
                                                }
                                                setForm({...form, ...patch});
                                            }}>
                                                <SelectTrigger data-testid="select-cartone-override"
                                                    className={`bg-slate-950/50 border-slate-800 mt-1 ${isChanged("cartone_id_override","cartone_id") ? "border-violet-500/80 border-2" : ""}`}>
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {cartoniAnag.map(c => <SelectItem key={c.id} value={c.id}>
                                                        <span className="font-mono text-amber-300 mr-2">{c.codice}</span>{c.descrizione}
                                                    </SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        )}
                                        <div>
                                            <Label className="text-xs text-slate-400">{packTitle} per pedana</Label>
                                            <Input data-testid="input-cpp-override" type="number" min="1"
                                                   value={form.cartoni_per_pedana_override}
                                                   onChange={e=>setForm({...form, cartoni_per_pedana_override: e.target.value, _cpp_manual: true})}
                                                   className={`bg-slate-950/50 border-slate-800 mt-1 font-mono ${isChanged("cartoni_per_pedana_override","cartoni_per_pedana_default") ? "border-violet-500/80 border-2" : ""}`} />
                                            {form.cartone_id_override && form.tipo_pedana_id_override && !form._cpp_manual && !form.cartoni_per_pedana_override && (
                                                <div className="text-[10px] text-amber-400 mt-1 flex items-start gap-1">
                                                    <AlertTriangle size={10} className="mt-0.5 shrink-0" />
                                                    <span>Nessuna capacità definita per questa combinazione cartone × pedana — apri Anagrafiche → Cartoni oppure inserisci il numero qui.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* RETTANGOLO 4 — Lavorazione */}
                                <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 space-y-3">
                                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-300">
                                        <CalendarClock size={12} /> Lavorazione
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="rect-lavorazione">
                                        <div>
                                            <Label className="text-xs text-slate-400">Macchina</Label>
                                            <Select value={form.impianto_id || ""} onValueChange={v=>setForm({...form, impianto_id: v})}>
                                                <SelectTrigger data-testid="select-impianto"
                                                    className="bg-slate-950/50 border-slate-800 mt-1">
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {(() => {
                                                        const compIds = selArt?.macchine_compatibili || [];
                                                        const list = compIds.length > 0 ? impianti.filter(i => compIds.includes(i.id)) : impianti;
                                                        if (list.length === 0) return <div className="px-2 py-1.5 text-xs text-slate-500">Nessuna macchina compatibile</div>;
                                                        return list.map(i => <SelectItem key={i.id} value={i.id}>
                                                            <span className="font-mono text-amber-300 mr-2">{i.codice}</span>{i.descrizione}
                                                            {i.piste_max && <span className="text-[10px] text-slate-500 ml-2">max {i.piste_max}p</span>}
                                                        </SelectItem>);
                                                    })()}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Piste</Label>
                                            <Select value={form.piste_override || ""} onValueChange={v=>setForm({...form, piste_override: v})}>
                                                <SelectTrigger data-testid="select-piste-override"
                                                    className={`bg-slate-950/50 border-slate-800 mt-1 ${isChanged("piste_override","piste_default") ? "border-violet-500/80 border-2" : ""}`}>
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {[1,2,3].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Turno</Label>
                                            <Select value={form.turno || ""} onValueChange={v=>setForm({...form, turno: v})}>
                                                <SelectTrigger data-testid="select-turno"
                                                    className="bg-slate-950/50 border-slate-800 mt-1">
                                                    <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                    {SHIFTS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label className="text-xs text-slate-400">Data lavorazione</Label>
                                            <Input data-testid="input-data-lavorazione" type="date"
                                                   value={form.data_lavorazione}
                                                   onChange={e=>setForm({...form, data_lavorazione: e.target.value})}
                                                   className="bg-slate-950/50 border-slate-800 mt-1 font-mono" />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-xs text-slate-400">Operatore {form.multi_operatore && <span className="text-amber-300">(chi inizia)</span>}</Label>
                                        <Select value={form.operatore_id || ""} onValueChange={v=>setForm({...form, operatore_id: v})}>
                                            <SelectTrigger data-testid="select-operatore"
                                                className="bg-slate-950/50 border-slate-800 mt-1">
                                                <SelectValue placeholder={form.multi_operatore ? "Opzionale — chi inizia" : "Chi deve fare la lavorazione?"} />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                                {operatori.length === 0 && <div className="px-2 py-1.5 text-xs text-slate-500">Nessun operatore registrato</div>}
                                                {operatori.map(op => <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer p-2 rounded-md bg-slate-950/30 border border-slate-800">
                                        <input type="checkbox" data-testid="check-multi-op"
                                               checked={!!form.multi_operatore}
                                               onChange={e=>setForm({...form, multi_operatore: e.target.checked})} />
                                        <Users size={14} className="text-amber-300" />
                                        <span><b>Multi-operatore</b> — ordine grande spalmato su più turni/operatori. Chiunque sulla stessa macchina lo vede e può lavorarci.</span>
                                    </label>
                                </div>

                                {/* Quantità + breakdown */}
                                <div>
                                    <Label className="text-xs text-slate-400">Quantità {isVas ? "vaschette" : "cartoni"} da produrre *</Label>
                                    <Input data-testid="input-cartoni-pianificati" type="number" step="1" min="1"
                                           value={form.cartoni_pianificati}
                                           onChange={e=>setForm({...form, cartoni_pianificati: e.target.value})}
                                           className="bg-slate-950/50 border-slate-800 mt-1 font-mono text-lg" placeholder="Es. 85" />
                                    {breakdown && (
                                        <div className="mt-2 flex items-center gap-2 text-sm" data-testid="breakdown-pedane">
                                            <Layers size={14} className="text-emerald-300" />
                                            <span className="text-slate-400">Uscita produzione:</span>
                                            <span className="font-mono text-emerald-300 font-bold">{breakdown}</span>
                                            {cppEff > 0 && (
                                                <span className="text-[10px] text-slate-500">({cppEff} {unitAbbr}/pedana)</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <div>
                            <Label className="text-xs text-slate-400">Note</Label>
                            <Textarea data-testid="input-order-note" value={form.note}
                                      onChange={e=>setForm({...form, note: e.target.value})}
                                      className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                        </div>
                    </div>
                    </fieldset>
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>{ setOpenDlg(false); setEditId(null); setReadOnlyView(false); setForm(emptyOrd);}} className="bg-transparent border-slate-700">{readOnlyView ? "Chiudi" : "Annulla"}</Button>
                        {editId && (
                            <Button variant="outline" onClick={()=>window.open(`/stampa-ordine/${editId}`, "_blank")}
                                    data-testid="button-print-a4-dialog"
                                    className="bg-transparent border-blue-500/40 text-blue-300 hover:bg-blue-500/10 gap-2">
                                <Printer size={14} /> Stampa A4
                            </Button>
                        )}
                        {!readOnlyView && (
                            <Button onClick={save} data-testid="button-save-order" disabled={!selArt || !qty || !wSumOk}
                                    className="bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-40">{editId ? "Aggiorna ordine" : "Crea ordine"}</Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
